import { faClose, faLayerGroup } from "@fortawesome/free-solid-svg-icons"
import { useEffect } from "react"
import { ButtonIcon } from "../../../components/Button/Button"
import { GenericDropDown } from "../../../components/Dropdown/Dropdown"
import ModalPortal from "../../../components/Modal/ModalPortal"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import THEME from "../../../configuration/Theme"
import { EEnableStatusType } from "../../../Enum/EEnableStatusType"
import { useCreateGenericFoodEntity } from "../../../hooks/useCreateGenericFoodEntity"
import { GenericFoodPupop } from "../GenericFoodPupop"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import { ComboEntity } from "../../../Objects/Combo/ComboEntity"
import { useCreateCombo } from "../../../hooks/Combo/useCreateCombo"
import { addtionalMemory, comboMemory, drinksMemory, mealsMemory } from "../../../Objects/Memory/Memory"
import { selectionMemory } from "../../../Objects/Memory/Memory"
import { useSaveCombo } from "../../../hooks/Combo/useSaveCombo"
import { useGetCombo } from "../../../hooks/Combo/useGetCombo"
import { EComboItemType } from "../../../Enum/EComboItemType"
import { Permissions } from "../../../Enum/PermissionsType"
import { SIZE } from "../../../configuration/Sizes"
import { CopyDraftAlert } from "../../../components/CopyDraftAlert/CopyDraftAlert"
import { useRemoveCombo } from "../../../hooks/Combo/useRemoveCombo"
import { PopupHelpButton } from "../../../components/Help/PopupHelpButton"
import { useTranslation } from "react-i18next"
import GetSpace from "../../../utils/space"
import { usePermission } from "../../../hooks/Permissions/usePermission"
import { useError } from "../../../context/useErrorContext"
import {
  ProductCompositionSelector,
  type ProductCompositionItem,
} from "../../../components/ProductCompositionSelector/ProductCompositionSelector"

interface ComboPopupProps {
  combo?: ComboEntity
  title: string
  onClose: () => void
  onCopy?: () => void
}
export const ComboPopup: React.FC<ComboPopupProps> = ({ combo, title, onClose, onCopy }) => {
  const { t } = useTranslation("genericFoodEntity")

  const isCopiedDraft = combo?.id === -1

  const canEdite = !usePermission(Permissions.CanEditeCombos)

  const { setError } = useError()

  const { handleSaveCombo } = useSaveCombo()

  const { handleRemoveCombo } = useRemoveCombo()

  const { handleGetCombo } = useGetCombo()

  const {
    name,
    description,
    status,
    coast,
    categoryId,
    additionalsAllowed,
    freeAdditionalsAllowed,
    allowedSelections,
    freeAddtionalsLimit,
    tags,
    imageAssetIds,
    imageUrls,
    hasStockControl,
    stockQuantity,
    hasNeedKitchen,
    hasPreparationTime,
    preparationTimeMinutes,
    isRodizioOnly,
    productOrderMode,
    reservationAllowedDays,
    reservationAllowedTimes,
    handleCategoryId,
    handleNameChange,
    handleCoastChange,
    handleDescriptionChange,
    handleStatusChange,
    handleFreeAddtionalsLimitChange,
    handleHasStockControlChange,
    handleHasNeedKitchenChange,
    handleHasPreparationTimeChange,
    handlePreparationTimeMinutesChange,
    handleIsRodizioOnlyChange,
    handleProductOrderModeChange,
    setReservationAllowedDays,
    setReservationAllowedTimes,
    handleStockQuantityChange,
    handleGenericFoodEntity,
    handleInitialGenericFoodEntity,
    setPromotionsIds,
    setTags,
    setImageAssetIds,
    setImageUrls,
    setImagesChanged,
    setAdditionalsAllowed,
    setFreeAdditionalsAllowed,
    setAllowedSelections,
  } = useCreateGenericFoodEntity(true)

  const { items, addMealItem, addDrinkItem, removeItem, updateQuantity, handleInitialValue, handleSubmitForm } =
    useCreateCombo()

  const mealOptions = (() => {
    const comboMealIds = new Set(comboMemory.listData.map((comboData) => comboData.mealId))

    return [
      { label: t("comboComposition.selectMeal"), value: "0" },
      ...mealsMemory.listData
        .filter((meal) => !comboMealIds.has(meal.id))
        .map((meal) => ({
          label: meal.genericFoodEntity.name,
          value: String(meal.id),
        })),
    ]
  })()

  const drinkOptions = [
    { label: t("comboComposition.selectDrink"), value: "0" },
    ...drinksMemory.listData.map((drink) => ({
      label: drink.genericFoodEntity.name,
      value: String(drink.id),
    })),
  ]

  const getItemLabel = (itemType: number, itemId: number) => {
    if (itemType === EComboItemType.Meal) {
      return mealsMemory.findById(itemId)?.genericFoodEntity.name ?? t("comboComposition.mealFallback", { id: itemId })
    }

    return drinksMemory.findById(itemId)?.genericFoodEntity.name ?? t("comboComposition.drinkFallback", { id: itemId })
  }

  const getItemMeta = (itemType: number, itemId: number) => {
    const sourceItem =
      itemType === EComboItemType.Meal
        ? mealsMemory.findById(itemId)?.genericFoodEntity
        : drinksMemory.findById(itemId)?.genericFoodEntity

    const fallbackIcon = itemType === EComboItemType.Meal ? "meal" : "drink"

    return {
      name: sourceItem?.name ?? getItemLabel(itemType, itemId),
      price: Number(sourceItem?.valueBase ?? 0),
      iconUrl: sourceItem?.iconUrl ?? "",
      fallbackIcon,
    }
  }

  const comboItemsTotalValue = items.reduce((total, item) => {
    const meta = getItemMeta(item.itemType, item.itemId)
    return total + meta.price * item.quantity
  }, 0)

  const comboSuggestedValue = comboItemsTotalValue * 0.9

  useEffect(() => {
    handleInitialValue(combo)

    handleInitialGenericFoodEntity(combo?.genericFoodEntity)
  }, [combo])

  const compositionItems: ProductCompositionItem[] = items.map((item, index) => {
    const meta = getItemMeta(item.itemType, item.itemId)

    return {
      key: String(index),
      name: meta.name,
      typeLabel:
        item.itemType === EComboItemType.Meal ? t("comboComposition.mealItem") : t("comboComposition.drinkItem"),
      quantity: item.quantity,
      price: meta.price,
      iconUrl: meta.iconUrl ? meta.iconUrl.toClientRequest() : undefined,
      fallbackIcon: meta.fallbackIcon === "meal" ? "\u{1F37D}\u{FE0F}" : "\u{1F964}",
    }
  })

  const handleCopyClick = async () => {
    onCopy?.()
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <div className={defaultStyle.alert}>
          <TitlePageResponsiveIconSmall
            title={title}
            icon={faLayerGroup}
            iconColor={THEME.colors.iconBGSecondaryColor}
            children={
              <div className={`${defaultStyle.ButtonsActions} flex-nowrap`}>
                {combo && (
                  <div className="w-[130px] mr-1">
                    <GenericDropDown
                      data={Object.mapEnumToStrings(EEnableStatusType, { exclude: [EEnableStatusType.All] })}
                      initialState={status}
                      disable={canEdite}
                      handlerFuncion={handleStatusChange}
                      descriptionKey="interactionDescriptions:pages.products.combo.comboPopup.select.select"
                    />
                  </div>
                )}

                <PopupHelpButton
                  helpKey="pages.products.combo.combopopup.1"
                  variant="global"
                  title={t("helpPopup:global.combo.title")}
                  description={t("helpPopup:global.combo.description")}
                  bullets={t("helpPopup:global.combo.bullets", { returnObjects: true }) as string[]}
                />

                <GetSpace right={5} />

                <ButtonIcon
                  sizeNumber={SIZE.buttonCloseIconSize}
                  bgColor={THEME.colors.buttonBGCloseColor}
                  iconColor={THEME.colors.buttonFGCloseColor}
                  icon={faClose}
                  onClick={onClose}
                  size="small"
                  descriptionKey="interactionDescriptions:pages.products.combo.comboPopup.button.onClose"
                />
              </div>
            }
          />

          {isCopiedDraft && <CopyDraftAlert />}

          <form
            onSubmit={(event) => {
              event.preventDefault()

              const hasInvalidReservationTime = reservationAllowedTimes.some((time) => {
                const value = time.trim()

                if (!/^\d{2}:\d{2}$/.test(value)) {
                  return true
                }

                const [hours, minutes] = value.split(":").map(Number)

                return (
                  Number.isNaN(hours) || Number.isNaN(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59
                )
              })

              if (
                productOrderMode !== 0 &&
                (reservationAllowedDays.length === 0 || reservationAllowedTimes.length === 0)
              ) {
                setError(t("general:label_invalid"), t("genericFoodEntity:label_reservation_config_required"))

                return
              }

              if (productOrderMode !== 0 && hasInvalidReservationTime) {
                setError(t("general:label_invalid"), t("genericFoodEntity:label_reservation_times_invalid"))

                return
              }

              if (Number(coast) <= 0) {
                setError(t("general:label_invalid"), t("label_product_value_must_be_greater_than_zero_combo"))

                return
              }

              const genericFoodEntity = handleGenericFoodEntity()

              const comboToSave = handleSubmitForm(genericFoodEntity)

              handleSaveCombo(comboToSave, () => {
                onClose()
                handleGetCombo(() => {})
              })
            }}
          >
            <GenericFoodPupop
              productType="combo"
              canEdite={canEdite}
              canSelectType={false}
              categoryId={categoryId}
              typeSelected={0}
              name={name}
              coast={coast}
              hasNeedKitchen={hasNeedKitchen}
              hasPreparationTime={hasPreparationTime}
              preparationTimeMinutes={preparationTimeMinutes}
              isRodizioOnly={isRodizioOnly}
              productOrderMode={productOrderMode}
              reservationAllowedDays={reservationAllowedDays}
              reservationAllowedTimes={reservationAllowedTimes}
              description={description}
              freeAddtionals={freeAdditionalsAllowed}
              freeAddtionalsLimit={freeAddtionalsLimit}
              tags={tags}
              imageAssetIds={imageAssetIds}
              imageUrls={imageUrls}
              onImagesChange={(ids, urls) => {
                setImageAssetIds(ids)

                setImageUrls(urls)

                setImagesChanged(true)
              }}
              hasStockControl={hasStockControl}
              stockQuantity={stockQuantity}
              addtionals={additionalsAllowed}
              selections={allowedSelections}
              genericFoodEntity={combo?.genericFoodEntity}
              removeAddtionals={(id) => setAdditionalsAllowed((e) => e.filter((a) => a.id !== id))}
              addAddtionals={(id) => {
                const additional = addtionalMemory.findById(id)

                if (!additional) {
                  return
                }

                setAdditionalsAllowed((e) => (e.some((a) => a.id === id) ? e : [...e, additional]))
              }}
              addFreeAddtionals={(id) => {
                const add = additionalsAllowed.find((e) => e.id === id)

                if (add) {
                  setFreeAdditionalsAllowed((e) => (e.some((x) => x.id === id) ? e : [...e, add]))
                }
              }}
              removeFreeAddtionals={(id) => setFreeAdditionalsAllowed((e) => e.filter((a) => a.id !== id))}
              addSelections={(id) => {
                const selection = selectionMemory.findById(id)
                if (!selection) return
                setAllowedSelections((e) => (e.some((x) => x.id === id) ? e : [...e, selection]))
              }}
              removeSelections={(id) => setAllowedSelections((e) => e.filter((x) => x.id !== id))}
              handleCategoryId={handleCategoryId}
              handleTypeSelectChange={() => {}}
              onRemove={async () => {
                if (!combo) {
                  return
                }

                await handleRemoveCombo(combo, () => handleGetCombo(onClose))
              }}
              setPromotions={setPromotionsIds}
              handleTagsChange={setTags}
              handleCoastChange={handleCoastChange}
              handleDescriptionChange={handleDescriptionChange}
              handleFreeAddtionalsLimitChange={handleFreeAddtionalsLimitChange}
              handleHasStockControlChange={handleHasStockControlChange}
              handleHasNeedKitchenChange={handleHasNeedKitchenChange}
              handleHasPreparationTimeChange={handleHasPreparationTimeChange}
              handlePreparationTimeMinutesChange={handlePreparationTimeMinutesChange}
              handleIsRodizioOnlyChange={handleIsRodizioOnlyChange}
              handleProductOrderModeChange={handleProductOrderModeChange}
              handleReservationAllowedDaysChange={setReservationAllowedDays}
              handleReservationAllowedTimesChange={setReservationAllowedTimes}
              handleStockQuantityChange={handleStockQuantityChange}
              handleNameChange={handleNameChange}
              showCategorySelector={false}
              savePermission={Permissions.CanEditeCombos}
              onCopy={handleCopyClick}
              customSectionBeforeTags={
                <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
                  <div className="mt-1 px-2">
                    <ProductCompositionSelector
                      title={t("comboComposition.title")}
                      description={t("comboComposition.description")}
                      groups={[
                        {
                          key: "meal",
                          label: t("comboComposition.meals"),
                          placeholder: t("comboComposition.selectMeal"),
                          options: mealOptions
                            .filter((option) => option.value !== "0")
                            .map((option) => ({ id: Number(option.value), label: option.label })),
                          selectDescriptionKey:
                            "interactionDescriptions:pages.products.combo.comboPopup.select.select2",
                          addDescriptionKey: "interactionDescriptions:pages.products.combo.comboPopup.button.faPlus",
                        },
                        {
                          key: "drink",
                          label: t("comboComposition.drinks"),
                          placeholder: t("comboComposition.selectDrink"),
                          options: drinkOptions
                            .filter((option) => option.value !== "0")
                            .map((option) => ({ id: Number(option.value), label: option.label })),
                          selectDescriptionKey:
                            "interactionDescriptions:pages.products.combo.comboPopup.select.select3",
                          addDescriptionKey: "interactionDescriptions:pages.products.combo.comboPopup.button.faPlus2",
                        },
                      ]}
                      items={compositionItems}
                      emptyLabel={t("comboComposition.empty")}
                      removeLabel={t("comboComposition.remove")}
                      quantityLabel={t("comboComposition.quantity")}
                      unitPriceLabel={t("comboComposition.unitPrice")}
                      totalPriceLabel={t("comboComposition.itemTotal")}
                      disabled={canEdite}
                      showControls={!canEdite}
                      summary={
                        items.length > 0 && !canEdite ? (
                          <div className="rounded-lg border border-orange-200 bg-orange-50 px-4 py-2 text-sm font-black text-orange-700">
                            <p>
                              {t("comboComposition.total", {
                                value: comboItemsTotalValue.toFixed(2).replace(".", ","),
                              })}
                            </p>
                            <p className="mt-1 text-xs font-semibold text-orange-600">
                              {t("comboComposition.suggested", {
                                value: comboSuggestedValue.toFixed(2).replace(".", ","),
                              })}
                            </p>
                          </div>
                        ) : undefined
                      }
                      onAdd={(groupKey, productId) => {
                        if (groupKey === "meal") addMealItem(productId)
                        else addDrinkItem(productId)
                      }}
                      onRemove={(itemKey) => removeItem(Number(itemKey))}
                      onQuantityChange={(itemKey, quantity) => updateQuantity(Number(itemKey), quantity)}
                    />
                  </div>
                </Animate>
              }
            />
          </form>
        </div>
      </div>
    </ModalPortal>
  )
}
